/**
 * Rules and Regulations Section
 *
 * CURRENT STATUS: Partial implementation - view-only for most fields
 *
 * EDIT MODE REQUIREMENTS:
 * The following fields need select dropdowns (currently display-only):
 * - USCG Group: Use useGetUSCGGroups() hook (API: GET /api/GetUscgGroups)
 * - IMDG Class: Use useGetIMDGCodeClasses() hook (API: GET /api/GetImdgCodeClasses)
 * - FOSFA: Enum select with values [0: Not Listed, 1: Listed, 2: Banned, 3: Restricted]
 * - EU: Enum select with values [0: Not Listed, 1: Listed, 2: Banned]
 * - NIOP: Enum select with values [0: Not Listed, 1: Listed, 2: Restricted, 3: Prohibited]
 * - OPA90: Boolean select [true: Yes, false: No]
 *
 * IBC Annex II fields (editable only when product.id === product.categoryId):
 * - All IBC fields need enum selects (see old template for enum values)
 * - Fire Protection: Needs checkboxes (A, B, C, D)
 *
 * Tripartite Agreement (currently using text inputs, should use):
 * - Expiry Date: Date picker component
 *
 * API HOOKS AVAILABLE:
 * - ✅ useGetUSCGGroups() - Created in src/api/useGetUSCGGroups.tsx
 * - ✅ useGetIMDGCodeClasses() - Created in src/api/useGetIMDGCodeClasses.tsx
 *
 * TODO:
 * 1. Create CompactSelect styled component
 * 2. Implement edit mode with select dropdowns for all enum fields
 * 3. Add category-level edit mode for IBC fields (when isProductCategory)
 * 4. Replace Tripartite Agreement Expiry Date text input with date picker
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useGetIMDGCodeClasses } from '../../../../api/useGetIMDGCodeClasses';
import { useGetUSCGGroups } from '../../../../api/useGetUSCGGroups';
import { useAuthStore } from '../../../../context/AuthContext';
import { useEditMode } from '../../../../context/EditModeContext';
import { useProductEdit } from '../../../../context/ProductEditContext';
import { ProductDetail } from '../../../../types/ProductTypes';
import InfoPopover from '../../../common/InfoPopover';
import { CompactSelect, CompactInput100 } from '../../EditableFields.styles';
import { SidebarCard, SidebarTitle, SidebarContent, SidebarItem } from '../../Sidebar.styles';
import { FosfaPopoverContent, EuPopoverContent, NiopPopoverContent } from './AllowancePopovers';
import {
    formatUSCGGroup,
    formatIMDGClass,
    formatAllowance,
    formatNIOPAllowance,
    formatYesNo,
    formatDate,
    formatMarpolRegulation,
    formatPollutionCategory,
    formatIBCHazards,
    formatShipType,
    formatTankType,
    formatTankVents,
    formatEnvironmentalControl,
    formatGauging,
    formatVapourDetection,
    formatFireProtection,
} from '../../../../utils/regulationFormatters';

interface RulesRegulationsSectionProps {
    product: ProductDetail;
}

const RulesRegulationsSection: React.FC<RulesRegulationsSectionProps> = ({ product }) => {
    const { isInEditMode } = useEditMode();
    const { canEdit } = useAuthStore();
    const productEditContext = useProductEdit();
    const mergePendingChanges = productEditContext?.mergePendingChanges;
    const getPendingChanges = productEditContext?.getPendingChanges;
    const showEditUI = canEdit && isInEditMode;

    // Check if this is a product category (product.id === product.categoryId)
    const isProductCategory = product.id === product.categoryId;

    // Fetch dropdown data
    const { uscgGroups } = useGetUSCGGroups();
    const { imdgCodeClasses } = useGetIMDGCodeClasses();

    const regs = product.regulations;
    const categoryRegs = product.category?.regulations;
    const ibc = categoryRegs?.ibcRegulation;
    const marpolReg = categoryRegs?.marpolRegulation;

    // Get effective regulations (pending changes merged over product data)
    const pendingChanges = getPendingChanges ? getPendingChanges() : {};
    const effectiveRegs = pendingChanges.regulations
        ? { ...regs, ...pendingChanges.regulations }
        : regs;
    const effectiveTripartite = effectiveRegs?.tripartiteAgreement;

    // Get effective category regulations (pending changes merged over product category data)
    const pendingCategory = pendingChanges.category as any;
    const effectiveCategoryRegs = pendingCategory?.regulations
        ? { ...categoryRegs, ...pendingCategory.regulations }
        : categoryRegs;
    const effectiveMarpolReg = effectiveCategoryRegs?.marpolRegulation ?? marpolReg;
    const effectiveIbc = effectiveCategoryRegs?.ibcRegulation || ibc;
    const effectivePollutionCategory = effectiveCategoryRegs?.marpolPollutionCategory ?? categoryRegs?.marpolPollutionCategory;

    // Compute virtual NIOP dropdown value from niop1Allowance / niop2Allowance
    // The API stores NIOP across two fields, but the UI shows a single dropdown:
    //   niop1Allowance=2 → "List 1 Acceptable" (dropdown value 2)
    //   niop2Allowance=2 → "List 2 Acceptable" (dropdown value 3)
    //   otherwise         → use niop1Allowance directly (1=Not Listed)
    const computeNiopDropdownValue = (n1: number | null | undefined, n2: number | null | undefined): number | '' => {
        if (n1 === 2) return 2;
        if (n2 === 2) return 3;
        if (n1 === 1) return 1;
        return '';
    };

    const effectiveNiopDropdownValue = computeNiopDropdownValue(
        effectiveRegs?.niop1Allowance,
        effectiveRegs?.niop2Allowance
    );

    // Dropdown options - API uses 1-based enum values
    const fosfaOptions = [
        { value: 3, text: 'Banned' },
        { value: 2, text: 'Acceptable' },
        { value: 1, text: 'Not Listed' },
    ];

    const euOptions = [
        { value: 3, text: 'Banned' },
        { value: 2, text: 'Acceptable' },
        { value: 1, text: 'Not Listed' },
    ];

    const niopOptions = [
        { value: 3, text: 'List 2 Acceptable' },
        { value: 2, text: 'List 1 Acceptable' },
        { value: 1, text: 'Not Listed' },
    ];

    const yesNoOptions = [
        { value: true, text: 'Yes' },
        { value: false, text: 'No' },
    ];

    // Fire Protection checkbox state (A=1, B=2, C=3, D=4)
    const [fireProtectionA, setFireProtectionA] = useState(false);
    const [fireProtectionB, setFireProtectionB] = useState(false);
    const [fireProtectionC, setFireProtectionC] = useState(false);
    const [fireProtectionD, setFireProtectionD] = useState(false);

    // Initialize checkbox state from product data
    useEffect(() => {
        const fireProtection = ibc?.fireProtection || [];
        setFireProtectionA(fireProtection.includes(1));
        setFireProtectionB(fireProtection.includes(2));
        setFireProtectionC(fireProtection.includes(3));
        setFireProtectionD(fireProtection.includes(4));
    }, [ibc?.fireProtection]);

    // Handle Fire Protection checkbox changes
    const handleFireProtectionChange = useCallback(
        (value: number, checked: boolean) => {
            if (!mergePendingChanges || !isProductCategory) return;

            const currentFireProtection = ibc?.fireProtection || [];
            let updatedFireProtection: number[];

            if (checked) {
                // Add value if not present
                updatedFireProtection = currentFireProtection.includes(value) ? currentFireProtection : [...currentFireProtection, value].sort();
            } else {
                // Remove value
                updatedFireProtection = currentFireProtection.filter((v) => v !== value);
            }

            // Update local state
            switch (value) {
                case 1:
                    setFireProtectionA(checked);
                    break;
                case 2:
                    setFireProtectionB(checked);
                    break;
                case 3:
                    setFireProtectionC(checked);
                    break;
                case 4:
                    setFireProtectionD(checked);
                    break;
            }

            // Update pending changes
            const currentPendingChanges = getPendingChanges ? getPendingChanges() : {};
            const currentCategory = currentPendingChanges.category || product.category;
            const currentCategoryRegulations = (currentCategory?.regulations || {}) as any;
            const currentIbcRegulation = currentCategoryRegulations.ibcRegulation || ibc || {};

            const updatedCategory = {
                ...currentCategory,
                regulations: {
                    ...currentCategoryRegulations,
                    ibcRegulation: {
                        ...currentIbcRegulation,
                        fireProtection: updatedFireProtection,
                    },
                },
            };

            console.log('[RulesRegulationsSection] Fire Protection changed:', updatedFireProtection);
            mergePendingChanges({ category: updatedCategory });
        },
        [mergePendingChanges, getPendingChanges, ibc, isProductCategory, product.category]
    );

    // Helper to merge a partial regulations change into pending changes
    const updateRegulations = useCallback(
        (partialRegs: Partial<ProductDetail['regulations']>) => {
            if (!mergePendingChanges) return;
            const currentPending = getPendingChanges ? getPendingChanges() : {};
            const currentRegs = currentPending.regulations || product.regulations || {};
            mergePendingChanges({
                regulations: {
                    ...currentRegs,
                    ...partialRegs,
                } as ProductDetail['regulations'],
            });
        },
        [mergePendingChanges, getPendingChanges, product.regulations]
    );

    // Helper to merge a partial category regulations change into pending changes (for IBC/Pollution fields)
    const updateCategoryRegulations = useCallback(
        (partialCatRegs: Record<string, any>) => {
            if (!mergePendingChanges) return;
            const currentPending = getPendingChanges ? getPendingChanges() : {};
            const currentCategory = (currentPending.category || product.category || {}) as any;
            const currentCatRegs = (currentCategory?.regulations || categoryRegs || {}) as any;
            mergePendingChanges({
                category: {
                    ...currentCategory,
                    regulations: {
                        ...currentCatRegs,
                        ...partialCatRegs,
                    },
                },
            } as any);
        },
        [mergePendingChanges, getPendingChanges, product.category, categoryRegs]
    );

    // Helper to update a single IBC regulation field
    const updateIbcField = useCallback(
        (ibcKey: string, value: number) => {
            if (!mergePendingChanges) return;
            const currentPending = getPendingChanges ? getPendingChanges() : {};
            const currentCategory = (currentPending.category || product.category || {}) as any;
            const currentCatRegs = (currentCategory?.regulations || categoryRegs || {}) as any;
            const currentIbc = currentCatRegs.ibcRegulation || ibc || {};
            mergePendingChanges({
                category: {
                    ...currentCategory,
                    regulations: {
                        ...currentCatRegs,
                        ibcRegulation: {
                            ...currentIbc,
                            [ibcKey]: value,
                        },
                    },
                },
            } as any);
        },
        [mergePendingChanges, getPendingChanges, product.category, categoryRegs, ibc]
    );

    // Helper: Check if tripartite has any values
    const hasTripartiteValue = (): boolean => {
        const tri = effectiveTripartite;
        if (!tri) return false;
        return !!(tri.reportingCountry || tri.expiryDate || tri.countries);
    };

    return (
        <SidebarCard>
            <SidebarTitle>Rules and Regulations</SidebarTitle>
            <SidebarContent>
                <SidebarItem>
                    <label title="United States Coast Guard grouping of hazardous products">USCG Group:</label>
                    {showEditUI ? (
                        <CompactSelect
                            aria-label="USCG Group"
                            value={effectiveRegs?.uscgGroup?.id || ''}
                            onChange={(e) => {
                                const selectedId = parseInt(e.target.value);
                                const selectedGroup = uscgGroups?.find((g) => g.id === selectedId);
                                updateRegulations({
                                    uscgGroup: selectedGroup || null,
                                } as any);
                            }}
                        >
                            <option value="">-- Select USCG Group --</option>
                            {Array.isArray(uscgGroups) &&
                                uscgGroups.map((group) => (
                                    <option key={group.id} value={group.id}>
                                        {group.number} - {group.name}
                                    </option>
                                ))}
                        </CompactSelect>
                    ) : (
                        <span>{formatUSCGGroup(regs?.uscgGroup)}</span>
                    )}
                </SidebarItem>
                <SidebarItem>
                    <label title="General Categories of Hazardous Cargo as defined in the International Maritime Dangerous Goods (IMDG) Code">IMDG Class:</label>
                    {showEditUI ? (
                        <CompactSelect
                            aria-label="IMDG Class"
                            value={effectiveRegs?.imdgCodeClass?.id || ''}
                            onChange={(e) => {
                                const selectedId = parseInt(e.target.value);
                                const selectedClass = imdgCodeClasses?.find((c) => c.id === selectedId);
                                updateRegulations({
                                    imdgCodeClass: selectedClass || null,
                                } as any);
                            }}
                        >
                            <option value="">-- Select IMDG Code Classification --</option>
                            {Array.isArray(imdgCodeClasses) &&
                                imdgCodeClasses.map((imdgClass) => (
                                    <option key={imdgClass.id} value={imdgClass.id}>
                                        {imdgClass.classification} - {imdgClass.description}
                                    </option>
                                ))}
                        </CompactSelect>
                    ) : (
                        <span>{formatIMDGClass(regs?.imdgCodeClass)}</span>
                    )}
                </SidebarItem>
                <SidebarItem>
                    <InfoPopover label="FOSFA:" title="FOSFA Allowance">
                        <FosfaPopoverContent />
                    </InfoPopover>
                    {showEditUI ? (
                        <CompactSelect
                            aria-label="FOSFA"
                            value={effectiveRegs?.fosfaAllowance ?? ''}
                            onChange={(e) => updateRegulations({ fosfaAllowance: parseInt(e.target.value) })}
                        >
                            {fosfaOptions.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                    {opt.text}
                                </option>
                            ))}
                        </CompactSelect>
                    ) : (
                        <span>{formatAllowance(regs?.fosfaAllowance)}</span>
                    )}
                </SidebarItem>
                <SidebarItem>
                    <InfoPopover label="EU:" title="EU Allowance">
                        <EuPopoverContent />
                    </InfoPopover>
                    {showEditUI ? (
                        <CompactSelect
                            aria-label="EU"
                            value={effectiveRegs?.euAllowance ?? ''}
                            onChange={(e) => updateRegulations({ euAllowance: parseInt(e.target.value) })}
                        >
                            {euOptions.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                    {opt.text}
                                </option>
                            ))}
                        </CompactSelect>
                    ) : (
                        <span>{formatAllowance(regs?.euAllowance)}</span>
                    )}
                </SidebarItem>
                <SidebarItem>
                    <InfoPopover label="NIOP:" title="NIOP Allowance">
                        <NiopPopoverContent />
                    </InfoPopover>
                    {showEditUI ? (
                        <CompactSelect
                            aria-label="NIOP"
                            value={effectiveNiopDropdownValue}
                            onChange={(e) => {
                                const dropdownValue = parseInt(e.target.value);
                                // Map virtual dropdown value back to niop1Allowance / niop2Allowance
                                // Mirrors the old AngularJS logic in product-details.component.js
                                if (dropdownValue === 2) {
                                    // List 1 Acceptable
                                    updateRegulations({ niop1Allowance: 2, niop2Allowance: 0 });
                                } else if (dropdownValue === 3) {
                                    // List 2 Acceptable
                                    updateRegulations({ niop1Allowance: 0, niop2Allowance: 2 });
                                } else {
                                    // Not Listed (1) or any other value
                                    updateRegulations({ niop1Allowance: dropdownValue, niop2Allowance: dropdownValue });
                                }
                            }}
                        >
                            {niopOptions.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                    {opt.text}
                                </option>
                            ))}
                        </CompactSelect>
                    ) : (
                        <span>{formatNIOPAllowance(regs?.niop1Allowance, regs?.niop2Allowance)}</span>
                    )}
                </SidebarItem>
                {(showEditUI || effectiveRegs?.isOpa90) && (
                    <SidebarItem>
                        <label title="Oil Pollution Act of 1990">OPA90:</label>
                        {showEditUI ? (
                            <CompactSelect
                                aria-label="OPA90"
                                value={effectiveRegs?.isOpa90 === true ? 'true' : effectiveRegs?.isOpa90 === false ? 'false' : ''}
                                onChange={(e) => updateRegulations({ isOpa90: e.target.value === 'true' })}
                            >
                                {yesNoOptions.map((opt) => (
                                    <option key={opt.value.toString()} value={opt.value.toString()}>
                                        {opt.text}
                                    </option>
                                ))}
                            </CompactSelect>
                        ) : (
                            <span>{formatYesNo(effectiveRegs?.isOpa90)}</span>
                        )}
                    </SidebarItem>
                )}

                {/* Tripartite Agreement Section */}
                {(showEditUI || hasTripartiteValue()) && (
                    <>
                        <SidebarTitle style={{ fontSize: '0.9rem', marginTop: '1rem' }}>Tripartite Agreement</SidebarTitle>
                        <SidebarItem>
                            <label title="Country which initially called for the tripartite agreement to be drafted">Reporting Country:</label>
                            {showEditUI ? (
                                <CompactInput100
                                    value={effectiveTripartite?.reportingCountry || ''}
                                    onChange={(e) =>
                                        updateRegulations({
                                            tripartiteAgreement: {
                                                ...effectiveTripartite,
                                                id: effectiveTripartite?.id || 0,
                                                lastUpdatedUtc: effectiveTripartite?.lastUpdatedUtc || new Date().toISOString(),
                                                reportingCountry: e.target.value,
                                                expiryDate: effectiveTripartite?.expiryDate || null,
                                                countries: effectiveTripartite?.countries || '',
                                                contains: effectiveTripartite?.contains || null,
                                            },
                                        })
                                    }
                                />
                            ) : (
                                <span>{effectiveTripartite?.reportingCountry || ''}</span>
                            )}
                        </SidebarItem>
                        <SidebarItem>
                            <label title="The last date which the tripartite agreement is valid">Expiry Date:</label>
                            {showEditUI ? (
                                <CompactInput100
                                    type="date"
                                    value={effectiveTripartite?.expiryDate ? effectiveTripartite.expiryDate.substring(0, 10) : ''}
                                    onChange={(e) =>
                                        updateRegulations({
                                            tripartiteAgreement: {
                                                ...effectiveTripartite,
                                                id: effectiveTripartite?.id || 0,
                                                lastUpdatedUtc: effectiveTripartite?.lastUpdatedUtc || new Date().toISOString(),
                                                reportingCountry: effectiveTripartite?.reportingCountry || null,
                                                expiryDate: e.target.value || null,
                                                countries: effectiveTripartite?.countries || '',
                                                contains: effectiveTripartite?.contains || null,
                                            },
                                        })
                                    }
                                />
                            ) : (
                                <span>{formatDate(effectiveTripartite?.expiryDate)}</span>
                            )}
                        </SidebarItem>
                        <SidebarItem>
                            <label title="Signatory countries partaking in the tripartite agreement">Countries:</label>
                            {showEditUI ? (
                                <CompactInput100
                                    value={effectiveTripartite?.countries || ''}
                                    onChange={(e) =>
                                        updateRegulations({
                                            tripartiteAgreement: {
                                                ...effectiveTripartite,
                                                id: effectiveTripartite?.id || 0,
                                                lastUpdatedUtc: effectiveTripartite?.lastUpdatedUtc || new Date().toISOString(),
                                                reportingCountry: effectiveTripartite?.reportingCountry || null,
                                                expiryDate: effectiveTripartite?.expiryDate || null,
                                                countries: e.target.value,
                                                contains: effectiveTripartite?.contains || null,
                                            },
                                        })
                                    }
                                />
                            ) : (
                                <span>{effectiveTripartite?.countries || ''}</span>
                            )}
                        </SidebarItem>
                    </>
                )}

                {/* IBC Annex II - Chapter 17/18 Section */}
                {(effectiveMarpolReg === 2 || effectiveMarpolReg === 3) && (
                    <>
                        <SidebarTitle style={{ fontSize: '0.9rem', marginTop: '1rem' }}>IBC {formatMarpolRegulation(effectiveMarpolReg)}</SidebarTitle>
                        <SidebarItem $variant={showEditUI && isProductCategory && !effectivePollutionCategory ? 'required' : undefined}>
                            <label title="Pollution category of product as specified in Marpol">Pollution Category{effectiveMarpolReg === 2 ? ' (c)' : ''}:{showEditUI && isProductCategory && <span style={{ color: '#e57373', marginLeft: 2 }}>*</span>}</label>
                            {showEditUI && isProductCategory ? (
                                <CompactSelect
                                    aria-label="Pollution Category"
                                    value={effectivePollutionCategory ?? ''}
                                    onChange={(e) => updateCategoryRegulations({ marpolPollutionCategory: parseInt(e.target.value) || 0 })}
                                >
                                    <option value="">-- Select --</option>
                                    <option value="1">X</option>
                                    <option value="2">Y</option>
                                    <option value="3">Z</option>
                                </CompactSelect>
                            ) : (
                                <span>{formatPollutionCategory(effectivePollutionCategory)}</span>
                            )}
                        </SidebarItem>
                    </>
                )}

                {effectiveMarpolReg === 2 && (effectiveIbc || (showEditUI && isProductCategory)) && (
                    <>
                        <SidebarItem $variant={showEditUI && isProductCategory && !effectiveIbc?.hazards ? 'required' : undefined}>
                            <label title="'S' means safety hazards, 'P' means pollution hazards, 'S/P' means both">Hazard Rules (d):{showEditUI && isProductCategory && <span style={{ color: '#e57373', marginLeft: 2 }}>*</span>}</label>
                            {showEditUI && isProductCategory ? (
                                <CompactSelect
                                    aria-label="Hazard Rules"
                                    value={effectiveIbc?.hazards ?? ''}
                                    onChange={(e) => updateIbcField('hazards', parseInt(e.target.value) || 0)}
                                >
                                    <option value="">-- Select --</option>
                                    <option value="1">S</option>
                                    <option value="2">P</option>
                                    <option value="3">S/P</option>
                                </CompactSelect>
                            ) : (
                                <span>{formatIBCHazards(effectiveIbc?.hazards)}</span>
                            )}
                        </SidebarItem>
                        <SidebarItem $variant={showEditUI && isProductCategory && !effectiveIbc?.shipType ? 'required' : undefined}>
                            <label title="Ref. IBC code, chapter 2.1. Ship type based on environmental and safety hazard severity">Ship Type (e):{showEditUI && isProductCategory && <span style={{ color: '#e57373', marginLeft: 2 }}>*</span>}</label>
                            {showEditUI && isProductCategory ? (
                                <CompactSelect
                                    aria-label="Ship Type"
                                    value={effectiveIbc?.shipType ?? ''}
                                    onChange={(e) => updateIbcField('shipType', parseInt(e.target.value) || 0)}
                                >
                                    <option value="">-- Select --</option>
                                    <option value="1">1</option>
                                    <option value="2">2</option>
                                    <option value="3">2k</option>
                                    <option value="4">3</option>
                                </CompactSelect>
                            ) : (
                                <span>{formatShipType(effectiveIbc?.shipType)}</span>
                            )}
                        </SidebarItem>
                        <SidebarItem $variant={showEditUI && isProductCategory && !effectiveIbc?.tankType ? 'required' : undefined}>
                            <label title="Ref. IBC code, chapter 4.1. Independent, integral, gravity or pressure tank">Tank Type (f):{showEditUI && isProductCategory && <span style={{ color: '#e57373', marginLeft: 2 }}>*</span>}</label>
                            {showEditUI && isProductCategory ? (
                                <CompactSelect
                                    aria-label="Tank Type"
                                    value={effectiveIbc?.tankType ?? ''}
                                    onChange={(e) => updateIbcField('tankType', parseInt(e.target.value) || 0)}
                                >
                                    <option value="">-- Select --</option>
                                    <option value="1">1G</option>
                                    <option value="2">2G</option>
                                    <option value="3">1P</option>
                                    <option value="4">2P</option>
                                </CompactSelect>
                            ) : (
                                <span>{formatTankType(effectiveIbc?.tankType)}</span>
                            )}
                        </SidebarItem>
                        <SidebarItem $variant={showEditUI && isProductCategory && !effectiveIbc?.tankVents ? 'required' : undefined}>
                            <label title="Ref. IBC code, chapter 8. Controlled, open or unknown venting requirements">Tank Ventilation (g):{showEditUI && isProductCategory && <span style={{ color: '#e57373', marginLeft: 2 }}>*</span>}</label>
                            {showEditUI && isProductCategory ? (
                                <CompactSelect
                                    aria-label="Tank Ventilation"
                                    value={effectiveIbc?.tankVents ?? ''}
                                    onChange={(e) => updateIbcField('tankVents', parseInt(e.target.value) || 0)}
                                >
                                    <option value="">-- Select --</option>
                                    <option value="1">Controlled</option>
                                    <option value="2">Open</option>
                                </CompactSelect>
                            ) : (
                                <span>{formatTankVents(effectiveIbc?.tankVents)}</span>
                            )}
                        </SidebarItem>
                        <SidebarItem $variant={showEditUI && isProductCategory && !effectiveIbc?.tankEnvironmentalControl ? 'required' : undefined}>
                            <label title="Ref. IBC code, chapter 9. Inerting, padding, drying, ventilation or no special requirements">Environment Control (h):{showEditUI && isProductCategory && <span style={{ color: '#e57373', marginLeft: 2 }}>*</span>}</label>
                            {showEditUI && isProductCategory ? (
                                <CompactSelect
                                    aria-label="Environment Control"
                                    value={effectiveIbc?.tankEnvironmentalControl ?? ''}
                                    onChange={(e) => updateIbcField('tankEnvironmentalControl', parseInt(e.target.value) || 0)}
                                >
                                    <option value="">-- Select --</option>
                                    <option value="1">Inert</option>
                                    <option value="2">Pad</option>
                                    <option value="3">Dry</option>
                                    <option value="4">Ventilation</option>
                                    <option value="5">No</option>
                                    <option value="6">Pad+(vent or inert)</option>
                                    <option value="7">Pad+inert</option>
                                    <option value="8">Vent or pad (gas)</option>
                                </CompactSelect>
                            ) : (
                                <span>{formatEnvironmentalControl(effectiveIbc?.tankEnvironmentalControl)}</span>
                            )}
                        </SidebarItem>
                        <SidebarItem $variant={showEditUI && isProductCategory && !effectiveIbc?.gauging ? 'required' : undefined}>
                            <label title="Ref. IBC code, chapter 13.1. O - Open, R - Restricted, C - Closed gauging">Gauging (j):{showEditUI && isProductCategory && <span style={{ color: '#e57373', marginLeft: 2 }}>*</span>}</label>
                            {showEditUI && isProductCategory ? (
                                <CompactSelect
                                    aria-label="Gauging"
                                    value={effectiveIbc?.gauging ?? ''}
                                    onChange={(e) => updateIbcField('gauging', parseInt(e.target.value) || 0)}
                                >
                                    <option value="">-- Select --</option>
                                    <option value="1">Open</option>
                                    <option value="2">Restricted</option>
                                    <option value="3">Closed</option>
                                </CompactSelect>
                            ) : (
                                <span>{formatGauging(effectiveIbc?.gauging)}</span>
                            )}
                        </SidebarItem>
                        <SidebarItem $variant={showEditUI && isProductCategory && !effectiveIbc?.vapourDetection ? 'required' : undefined}>
                            <label title="Ref. IBC code, chapter 13.2. F - Flammable vapours, T - Toxic vapours, No - No special requirements">Vapour Detection (k):{showEditUI && isProductCategory && <span style={{ color: '#e57373', marginLeft: 2 }}>*</span>}</label>
                            {showEditUI && isProductCategory ? (
                                <CompactSelect
                                    aria-label="Vapour Detection"
                                    value={effectiveIbc?.vapourDetection ?? ''}
                                    onChange={(e) => updateIbcField('vapourDetection', parseInt(e.target.value) || 0)}
                                >
                                    <option value="">-- Select --</option>
                                    <option value="1">F</option>
                                    <option value="2">T</option>
                                    <option value="3">F/T</option>
                                    <option value="4">No</option>
                                </CompactSelect>
                            ) : (
                                <span>{formatVapourDetection(effectiveIbc?.vapourDetection)}</span>
                            )}
                        </SidebarItem>
                        <SidebarItem>
                            <label title="Ref. IBC code, chapter 11. A - Alcohol-resistant foam, B - Regular foam, C - Water-spray, D - Dry chemical">Fire Protection (l):</label>
                            {showEditUI && isProductCategory ? (
                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
                                        <input
                                            type="checkbox"
                                            checked={fireProtectionA}
                                            onChange={(e) => handleFireProtectionChange(1, e.target.checked)}
                                        />
                                        A
                                    </label>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
                                        <input
                                            type="checkbox"
                                            checked={fireProtectionB}
                                            onChange={(e) => handleFireProtectionChange(2, e.target.checked)}
                                        />
                                        B
                                    </label>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
                                        <input
                                            type="checkbox"
                                            checked={fireProtectionC}
                                            onChange={(e) => handleFireProtectionChange(3, e.target.checked)}
                                        />
                                        C
                                    </label>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
                                        <input
                                            type="checkbox"
                                            checked={fireProtectionD}
                                            onChange={(e) => handleFireProtectionChange(4, e.target.checked)}
                                        />
                                        D
                                    </label>
                                </div>
                            ) : (
                                <span>{formatFireProtection(effectiveIbc?.fireProtection)}</span>
                            )}
                        </SidebarItem>
                        <SidebarItem>
                            <label title="Ref. IBC code, chapter 14.3. Whether every person on board must be provided with respiratory and eye protection for emergency escape">Emergency Equipment (n):</label>
                            {showEditUI && isProductCategory ? (
                                <CompactSelect
                                    aria-label="Emergency Equipment"
                                    value={effectiveIbc?.requiresEmergencyEquipment === true ? 'true' : effectiveIbc?.requiresEmergencyEquipment === false ? 'false' : ''}
                                    onChange={(e) => updateIbcField('requiresEmergencyEquipment', e.target.value === 'true' ? 1 : 0)}
                                >
                                    <option value="false">No</option>
                                    <option value="true">Yes</option>
                                </CompactSelect>
                            ) : (
                                <span>{formatYesNo(effectiveIbc?.requiresEmergencyEquipment)}</span>
                            )}
                        </SidebarItem>
                    </>
                )}
            </SidebarContent>
        </SidebarCard>
    );
};

export default RulesRegulationsSection;
